import React, { useState } from 'react';
import { useDashboard, RangeConfig } from '../DashboardContext';

interface Props { periodLabel: string; }

const PRESETS: Array<{ name: string; config: RangeConfig | null }> = [
    { name: 'Standard',  config: { range1: { label: 'Recent (Last 10 Yrs)', upperBound: 10 }, range2: { label: 'Mid (10–20 Yrs)',    upperBound: 20 }, range3: { label: 'Senior (20+ Yrs)' } } },
    { name: 'Compact',   config: { range1: { label: 'Recent (Last 5 Yrs)',  upperBound: 5  }, range2: { label: 'Mid (5–15 Yrs)',     upperBound: 15 }, range3: { label: 'Senior (15+ Yrs)' } } },
    { name: 'Long View', config: { range1: { label: 'Recent (Last 15 Yrs)', upperBound: 15 }, range2: { label: 'Mid (15–30 Yrs)',    upperBound: 30 }, range3: { label: 'Senior (30+ Yrs)' } } },
    { name: 'Custom',    config: null }, // null = clear all fields for manual entry
];

const EMPTY_CONFIG: RangeConfig = {
    range1: { label: '', upperBound: 1 },
    range2: { label: '', upperBound: 2 },
    range3: { label: '' },
};

const FilterBar: React.FC<Props> = ({ periodLabel }) => {
    const { batchRange, setBatchRange, instituteLoaded, rangeConfig, setRangeConfig } = useDashboard();

    const [showModal, setShowModal] = useState(false);
    const [draft, setDraft] = useState<RangeConfig>(rangeConfig);
    const [error, setError] = useState('');

    const openModal = () => { setDraft({ ...rangeConfig }); setError(''); setShowModal(true); };

    const applyPreset = (cfg: RangeConfig | null) => {
        // null = Custom: clear all fields for manual entry
        setDraft(cfg ?? EMPTY_CONFIG);
        setError('');
    };

    const handleSave = () => {
        if (!draft.range1.label.trim() || !draft.range2.label.trim() || !draft.range3.label.trim()) {
            setError('All labels are required.'); return;
        }
        if (draft.range1.upperBound < 1) {
            setError('Range 1 must be at least 1 year.'); return;
        }
        if (draft.range2.upperBound <= draft.range1.upperBound) {
            setError('Range 2 must be greater than Range 1.'); return;
        }
        setRangeConfig(draft);
        setBatchRange('all');
        setShowModal(false);
    };

    const chips = [
        { val: 'all',    label: 'All Batches' },
        { val: 'range1', label: rangeConfig.range1.label },
        { val: 'range2', label: rangeConfig.range2.label },
        { val: 'range3', label: rangeConfig.range3.label },
    ];

    return (
        <>
            <div className="bg-white border rounded px-3 py-2 mb-3 d-flex align-items-center flex-wrap gap-2">
                <span className="text-muted small fw-semibold me-1">
                    <i className="bi bi-funnel me-1"></i>{periodLabel}
                </span>
                <div className="vr mx-2 opacity-25"></div>
                <span className="text-muted small fw-semibold">Batch Range:</span>

                {chips.map(chip => (
                    <button
                        key={chip.val}
                        className={`btn btn-sm py-0 px-2 ${batchRange === chip.val ? 'btn-dark' : 'btn-outline-secondary'}`}
                        disabled={!instituteLoaded}
                        onClick={() => setBatchRange(chip.val)}
                        style={{ fontSize: 12 }}
                    >
                        {chip.label}
                    </button>
                ))}

                <button
                    className="btn btn-sm btn-outline-secondary ms-1"
                    style={{ border: '1px dashed', fontSize: 12 }}
                    disabled={!instituteLoaded}
                    onClick={openModal}
                >
                    ✏️ Edit Ranges
                </button>
            </div>

            {showModal && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title font-16">Edit Batch Ranges</h5>
                                <button className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">

                                {/* Quick Presets — 4 buttons including Custom */}
                                <div className="mb-3">
                                    <div className="small fw-semibold text-muted mb-2">Quick Presets</div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {PRESETS.map(p => (
                                            <button key={p.name}
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => applyPreset(p.config)}>
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Range 1 */}
                                <div className="mb-3">
                                    <div className="small fw-semibold mb-1">
                                        <span style={{ color: '#22c55e' }}>●</span> Range 1
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-7">
                                            <input className="form-control form-control-sm" placeholder="Label (max 30 chars)" maxLength={30}
                                                value={draft.range1.label}
                                                onChange={e => setDraft({ ...draft, range1: { ...draft.range1, label: e.target.value } })} />
                                        </div>
                                        <div className="col-5">
                                            <div className="input-group input-group-sm">
                                                <input type="number" className="form-control" min={1} max={99}
                                                    value={draft.range1.upperBound}
                                                    onChange={e => setDraft({ ...draft, range1: { ...draft.range1, upperBound: Number(e.target.value) } })} />
                                                <span className="input-group-text" style={{ fontSize: 11 }}>yrs ago</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Range 2 — with read-only lower bound prefix (R1+1) per spec */}
                                <div className="mb-3">
                                    <div className="small fw-semibold mb-1">
                                        <span style={{ color: '#f59e0b' }}>●</span> Range 2
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-7">
                                            <input className="form-control form-control-sm" placeholder="Label (max 30 chars)" maxLength={30}
                                                value={draft.range2.label}
                                                onChange={e => setDraft({ ...draft, range2: { ...draft.range2, label: e.target.value } })} />
                                        </div>
                                        <div className="col-5">
                                            <div className="input-group input-group-sm">
                                                {/* Read-only lower bound = Range1Upper + 1 */}
                                                <span className="input-group-text" style={{ fontSize: 11 }}>
                                                    {draft.range1.upperBound + 1}–
                                                </span>
                                                <input type="number" className="form-control" min={draft.range1.upperBound + 1} max={99}
                                                    value={draft.range2.upperBound}
                                                    onChange={e => setDraft({ ...draft, range2: { ...draft.range2, upperBound: Number(e.target.value) } })} />
                                                <span className="input-group-text" style={{ fontSize: 11 }}>yrs</span>
                                            </div>
                                        </div>
                                    </div>
                                    {draft.range2.upperBound <= draft.range1.upperBound && (
                                        <div className="text-danger small mt-1">Range 2 must be greater than Range 1</div>
                                    )}
                                </div>

                                {/* Range 3 — label editable, upper bound auto-calculated */}
                                <div className="mb-3">
                                    <div className="small fw-semibold mb-1">
                                        <span style={{ color: '#6366f1' }}>●</span> Range 3 (auto)
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-7">
                                            <input className="form-control form-control-sm" placeholder="Label (max 30 chars)" maxLength={30}
                                                value={draft.range3.label}
                                                onChange={e => setDraft({ ...draft, range3: { label: e.target.value } })} />
                                        </div>
                                        <div className="col-5">
                                            <input className="form-control form-control-sm"
                                                value={`> ${draft.range2.upperBound} yrs (auto)`}
                                                readOnly
                                                style={{ background: '#f8f9fa', fontSize: 11, color: '#6c757d' }} />
                                        </div>
                                    </div>
                                </div>

                                {error && <div className="alert alert-danger py-2 small mb-0">{error}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn btn-green btn-sm" onClick={handleSave}>Save Ranges</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FilterBar;
